import { lazy, Suspense, useEffect } from "react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@/components/ThemeProvider";

import HomePage from "@/pages/HomePage";
import CalculatorPage from "@/pages/CalculatorPage";

const SalaryAmountPage = lazy(() => import("@/pages/SalaryAmountPage"));
const USStateSalaryPage = lazy(() => import("@/pages/USStateSalaryPage"));
const ComparePage = lazy(() => import("@/pages/ComparePage"));
const BudgetPlannerPage = lazy(() => import("@/pages/BudgetPlannerPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogArticlePage = lazy(() => import("@/pages/BlogArticlePage"));
const EmbedPage = lazy(() => import("@/pages/EmbedPage"));
const EmbedPartnerPage = lazy(() => import("@/pages/embed/PartnerPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const CookiePolicyPage = lazy(() => import("@/pages/CookiePolicyPage"));
const DisclaimerPage = lazy(() => import("@/pages/DisclaimerPage"));
const DMCAPage = lazy(() => import("@/pages/DMCAPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const MethodologyPage = lazy(() => import("@/pages/MethodologyPage"));
const NotFound = lazy(() => import("@/pages/not-found"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={HomePage} />
        <Route path="/salary/us/:state/:amount" component={USStateSalaryPage} />
        <Route path="/salary/:country/:amount" component={SalaryAmountPage} />
        <Route path="/calculator/:country" component={CalculatorPage} />
        <Route path="/compare" component={ComparePage} />
        <Route path="/budget-planner" component={BudgetPlannerPage} />
        <Route path="/blog" component={BlogPage} />
        <Route path="/blog/:slug" component={BlogArticlePage} />
        <Route path="/embed" component={EmbedPage} />
        <Route path="/embed/partners" component={EmbedPartnerPage} />
        <Route path="/privacy-policy" component={PrivacyPolicyPage} />
        <Route path="/terms-of-service" component={TermsOfServicePage} />
        <Route path="/cookie-policy" component={CookiePolicyPage} />
        <Route path="/disclaimer" component={DisclaimerPage} />
        <Route path="/dmca" component={DMCAPage} />
        <Route path="/about" component={AboutPage} />
        <Route path="/contact" component={ContactPage} />
        <Route path="/methodology" component={MethodologyPage} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    const el = document.getElementById("__seo__");
    if (el) el.remove();
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TooltipProvider>
            <Toaster />
            <Router />
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
